import React, { useRef, useState, useEffect } from "react";
import { Phone, MapPin, Clock, Mail, Instagram, Twitter, Send } from "lucide-react";

// TikTok Icon
const TikTokIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.18 8.18 0 004.79 1.52V6.76a4.85 4.85 0 01-1.02-.07z"/>
  </svg>
);

// LinkedIn Icon
const LinkedInIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z"/>
    <circle cx="4" cy="4" r="2"/>
  </svg>
);
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const useInView = (threshold = 0.1) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
};

const Contact = ({ contact }) => {
  const [titleRef, titleInView] = useInView(0.2);
  const [form, setForm] = useState({ name: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const phone1 = contact?.phone1 || "0555661937";
  const phone2 = contact?.phone2 || "0553972038";
  const whatsapp = contact?.whatsapp || "966555661937";
  const address = contact?.address || "جازان - طريق المدينة المنورة - حي المحمدية";
  const hours = contact?.working_hours || "الأحد - الخميس: 9:00 صباحاً - 5:00 مساءً";
  const email = contact?.email || "info@alwasimre.com";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.message) return;
    setSending(true);
    setError("");
    try {
      await axios.post(`${API}/inquiry`, form);
      setSent(true);
      setForm({ name: "", phone: "", message: "" });
    } catch {
      setError("حدث خطأ، يرجى المحاولة مجدداً");
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 relative overflow-hidden"
      style={{ background: "linear-gradient(135deg, #0A1128 0%, #173269 100%)" }}
    >
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-5"
        style={{ background: "radial-gradient(circle, #ecc775, transparent)" }}></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full opacity-5"
        style={{ background: "radial-gradient(circle, #ecc775, transparent)" }}></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div ref={titleRef} className={`text-center mb-16 scroll-hidden ${titleInView ? "scroll-visible" : ""}`}>
          <p className="text-[#ecc775] font-bold text-sm uppercase tracking-widest mb-3">تواصل معنا</p>
          <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
            {contact?.title || "تواصل معنا"}
          </h2>
          <div className="section-divider mx-auto mb-6"></div>
          <p className="text-white/70 text-lg max-w-2xl mx-auto">
            {contact?.subtitle || "نحن هنا لمساعدتك"}، تواصل معنا الآن ودعنا نحقق أهدافك العقارية
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div className="space-y-6">
            <ContactItem Icon={Phone} label="الهاتف" value={`${phone1} | ${phone2}`} href={`tel:${phone1}`} />
            <ContactItem Icon={Mail} label="البريد الإلكتروني" value={email} href={`mailto:${email}`} />
            <ContactItem Icon={MapPin} label="الموقع" value={address} />
            <ContactItem Icon={Clock} label="ساعات العمل" value={hours} />

            {/* Social */}
            <div className="flex gap-3 mt-6 flex-wrap">
              {contact?.social_instagram && (
                <a href={`https://instagram.com/${contact.social_instagram.replace("@", "")}`}
                  target="_blank" rel="noopener noreferrer"
                  title="انستقرام"
                  className="w-12 h-12 bg-white/10 border border-white/20 flex items-center justify-center text-[#ecc775] hover:bg-[#ecc775] hover:text-[#173269] transition-all duration-300">
                  <Instagram size={20} />
                </a>
              )}
              {contact?.social_twitter && (
                <a href={`https://twitter.com/${contact.social_twitter.replace("@", "")}`}
                  target="_blank" rel="noopener noreferrer"
                  title="تويتر / X"
                  className="w-12 h-12 bg-white/10 border border-white/20 flex items-center justify-center text-[#ecc775] hover:bg-[#ecc775] hover:text-[#173269] transition-all duration-300">
                  <Twitter size={20} />
                </a>
              )}
              {contact?.social_tiktok && (
                <a href={`https://tiktok.com/@${contact.social_tiktok.replace("@", "")}`}
                  target="_blank" rel="noopener noreferrer"
                  title="تيك توك"
                  className="w-12 h-12 bg-white/10 border border-white/20 flex items-center justify-center text-[#ecc775] hover:bg-[#ecc775] hover:text-[#173269] transition-all duration-300">
                  <TikTokIcon size={20} />
                </a>
              )}
              {contact?.social_linkedin && (
                <a href={`https://linkedin.com/in/${contact.social_linkedin.replace("@", "")}`}
                  target="_blank" rel="noopener noreferrer"
                  title="لينكدإن"
                  className="w-12 h-12 bg-white/10 border border-white/20 flex items-center justify-center text-[#ecc775] hover:bg-[#ecc775] hover:text-[#173269] transition-all duration-300">
                  <LinkedInIcon size={20} />
                </a>
              )}
            </div>

            {/* WhatsApp CTA */}
            <a
              href={`https://wa.me/${whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-btn"
              className="flex items-center gap-4 bg-[#25d366] text-white px-6 py-4 font-bold text-lg hover:bg-[#1da851] transition-all duration-300 shadow-lg shadow-green-500/30 rounded-sm mt-4"
            >
              <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.465 3.488"/>
              </svg>
              تواصل عبر واتساب
            </a>
          </div>

          {/* Contact Form */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 p-8 rounded-sm">
            <h3 className="text-white font-bold text-2xl mb-6">أرسل لنا رسالة</h3>

            {sent ? (
              <div className="bg-green-500/20 border border-green-500/40 text-green-300 p-6 rounded-sm text-center">
                <div className="text-4xl mb-3">✓</div>
                <p className="font-bold text-lg">تم إرسال رسالتك بنجاح!</p>
                <p className="text-sm mt-2 opacity-80">سنتواصل معك في أقرب وقت ممكن</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="text-white/70 text-sm font-semibold mb-2 block">الاسم الكامل</label>
                  <input
                    data-testid="contact-name"
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="أدخل اسمك الكامل"
                    className="w-full bg-white/10 border border-white/20 text-white placeholder-white/40 px-4 py-3 focus:outline-none focus:border-[#ecc775] transition-colors rounded-sm font-cairo"
                    required
                  />
                </div>
                <div>
                  <label className="text-white/70 text-sm font-semibold mb-2 block">رقم الجوال</label>
                  <input
                    data-testid="contact-phone"
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="05xxxxxxxx"
                    className="w-full bg-white/10 border border-white/20 text-white placeholder-white/40 px-4 py-3 focus:outline-none focus:border-[#ecc775] transition-colors rounded-sm font-cairo"
                    dir="ltr"
                    required
                  />
                </div>
                <div>
                  <label className="text-white/70 text-sm font-semibold mb-2 block">الرسالة</label>
                  <textarea
                    data-testid="contact-message"
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="اكتب رسالتك هنا..."
                    rows={4}
                    className="w-full bg-white/10 border border-white/20 text-white placeholder-white/40 px-4 py-3 focus:outline-none focus:border-[#ecc775] transition-colors rounded-sm font-cairo resize-none"
                    required
                  />
                </div>
                {error && <p className="text-red-400 text-sm">{error}</p>}
                <button
                  data-testid="contact-submit"
                  type="submit"
                  disabled={sending}
                  className="w-full bg-[#ecc775] text-[#173269] py-4 font-black text-lg flex items-center justify-center gap-3 hover:bg-white transition-all duration-300 disabled:opacity-60 rounded-sm shadow-lg"
                >
                  {sending ? "جاري الإرسال..." : (<><Send size={18} />إرسال الرسالة</>)}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const ContactItem = ({ Icon, label, value, href }) => (
  <div className="flex gap-4 items-start">
    <div className="w-12 h-12 bg-[#ecc775]/15 border border-[#ecc775]/30 flex items-center justify-center flex-shrink-0">
      <Icon size={20} className="text-[#ecc775]" />
    </div>
    <div>
      <p className="text-white/50 text-xs font-semibold uppercase tracking-wider mb-1">{label}</p>
      {href ? (
        <a href={href} className="text-white font-semibold hover:text-[#ecc775] transition-colors">{value}</a>
      ) : (
        <p className="text-white font-semibold">{value}</p>
      )}
    </div>
  </div>
);

export default Contact;
